import type { PathItemObject } from '@jotta/types/Files'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import Debug from 'debug'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { WopiView } from './WopiView'
import { useMutation } from '@connectrpc/connect-query'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { updateWopiConsent } from '@jotta/grpc-connect-openapi/wopiQuery'
import { authTransport } from '@jotta/grpc-connect-client/transport'
import { WopiActionType, WopiConsent } from '@jotta/grpc-connect-openapi/wopi'
import { t, Trans } from '@lingui/macro'
import { Dialog } from '@jotta/ui/Dialog'
import { useUsername } from '@jotta/grpc-connect-client/customer'
import { getLinksQuery, useWopiLinks } from './useWopiLinks'

const debug = Debug('jotta:files:WopiViewContainer')

export type WopiPostMessageType =
  | 'File_Rename'
  // The UI_Sharing message is posted when the user activates the Share UI in Office for the web.
  // The host should use this message to trigger any custom sharing UI.
  | 'UI_Sharing'
  // The UI_Close message is posted when the Office for the web application is closing, either due to an error or a user action
  | 'UI_Close'
  // The UI_Edit message is posted when the user activates the Edit UI in Office for the web. This UI is only visible when using the view action.
  | 'UI_Edit'
export type WopiPostMessage = {
  MessageId: WopiPostMessageType
  SendTime: number
  Values: Record<string, string>
}

export function EnableWopi() {
  const { mutate, isPending } = useMutation(updateWopiConsent, {
    transport: authTransport,
    onSuccess: () => getLinksQuery.invalidate(),
  })

  return (
    <Dialog
      className="!m-0"
      title={t`Use Office Online to preview and edit Office documents`}
      buttons={[
        {
          type: 'primary',
          label: t`Enable`,
          loading: isPending,
          action: () =>
            mutate({
              consent: WopiConsent.ACCEPTED,
            }),
        },
      ]}
    >
      <Trans>
        Office documents you preview or edit will be sent to Microsoft Office
        Online for processing while you preview or edit them. You can disable
        Office Online at any time from the settings page.
      </Trans>
    </Dialog>
  )
}

export function DisabledByOwner() {
  return (
    <Dialog className="!m-0" title={t`Microsoft Office Online is disabled`}>
      <Trans>
        The owner of this file has disabled access to Microsoft Office Online.
      </Trans>
    </Dialog>
  )
}

export const WopiViewContainer = ({ item }: { item: PathItemObject }) => {
  const [search] = useSearchParams()
  const filePath = item.path
  const fileExtension = filePath.split('.').pop()?.toLowerCase() || ''
  const username = useUsername()

  const isTestFile =
    ['wopitest', 'wopitestx'].includes(fileExtension) && WopiActionType.VIEW
  const isNewFile = !item.size && WopiActionType.EDITNEW
  const isEditAction =
    !isNewFile && search.get('action') === 'edit' && WopiActionType.EDIT
  const isLegacyFormat =
    isEditAction &&
    ['doc', 'ppt', 'xls'].includes(fileExtension) &&
    WopiActionType.CONVERT
  const action: WopiActionType =
    isTestFile ||
    isNewFile ||
    isLegacyFormat ||
    isEditAction ||
    WopiActionType.EMBEDVIEW
  const showWopiEditView = action !== WopiActionType.EMBEDVIEW

  useEffect(() => {
    console.group('WopiView')
    return () => {
      console.groupEnd()
    }
  }, [])

  const { data, isRefetching } = useWopiLinks(item, action)

  const result = data?.result

  if (!result || isRefetching) {
    return <LoadingOverlay open />
  }

  switch (result.case) {
    case undefined:
      return <LoadingOverlay open />
    case 'links':
      return (
        <WopiView
          wopiLinks={result.value}
          item={item}
          onWopiEvent={(e, data) => {
            debug('onWopiEvent', e, data)
          }}
          showWopiEditView={showWopiEditView}
        />
      )
    case 'disabledByOwner':
      return (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {item.owner === username ? <EnableWopi /> : <DisabledByOwner />}
        </div>
      )
    default:
      exhaustiveGuard(result)
  }
}
